/**
 * Централизованная система управления ключами запросов React Query
 *
 * Преимущества:
 * - Единая точка определения всех ключей
 * - Типобезопасность
 * - Легкая инвалидация связанных запросов
 * - Предотвращение опечаток в ключах
 */

export const queryKeys = {
  // Activities
  activities: {
    all: ['activities'] as const,
    lists: () => [...queryKeys.activities.all, 'list'] as const,
    list: (page: number, filters?: Record<string, any>) =>
      [...queryKeys.activities.lists(), page, filters] as const,
    detail: (id: string) => [...queryKeys.activities.all, id] as const,
  },

  // Cars
  cars: {
    all: ['cars'] as const,
    lists: () => [...queryKeys.cars.all, 'list'] as const,
    list: (params?: Record<string, any>) =>
      [...queryKeys.cars.all, params] as const,
    detail: (id: string) => [...queryKeys.cars.all, id] as const,
    enums: () => [...queryKeys.cars.all, 'enums'] as const,
  },

  // Car Models
  carModels: {
    all: ['car-models'] as const,
    lists: () => [...queryKeys.carModels.all, 'list'] as const,
    list: (page?: number) =>
      [...queryKeys.carModels.all, page] as const,
    detail: (id: string) => [...queryKeys.carModels.all, id] as const,
  },

  // Tech Profiles
  techProfiles: {
    all: ['tech-profiles'] as const,
    lists: () => [...queryKeys.techProfiles.all, 'list'] as const,
    list: () => [...queryKeys.techProfiles.lists()] as const,
  },

  // Car Calendar & Prices
  carCalendar: {
    all: ['carCalendar'] as const,
    prices: (carId: string, year: number, month: number) =>
      [...queryKeys.carCalendar.all, carId, year, month] as const,
  },

  // Car Reports
  carReports: {
    all: ['reports'] as const,
    lists: () => [...queryKeys.carReports.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.carReports.all, id] as const,
    techProfile: (carId: string) =>
      [...queryKeys.carReports.all, 'tech-profile', carId] as const,
    rentingReports: (rentingId: string) =>
      [...queryKeys.carReports.all, 'renting', rentingId] as const,
  },

  // Clients
  clients: {
    all: ['clients'] as const,
    lists: () => [...queryKeys.clients.all, 'list'] as const,
    list: (page: number, filters?: Record<string, any>) =>
      [...queryKeys.clients.lists(), page, filters] as const,
    detail: (id: string) => [...queryKeys.clients.all, id] as const,
  },

  // Rentings
  rentings: {
    all: ['rentings'] as const,
    lists: () => [...queryKeys.rentings.all, 'list'] as const,
    list: (page: number, filters?: Record<string, any>) =>
      [...queryKeys.rentings.lists(), page, filters] as const,
    detail: (id: string) => [...queryKeys.rentings.all, id] as const,
  },

  // Profile
  profile: {
    userProfile: (userId: string) => ['userProfile', userId] as const,
    employeeProfile: (userId: string) => ['employeeProfile', userId] as const,
    employeeRole: (roleId: string) => ['employeeRole', roleId] as const,
  },

  // Team
  team: {
    all: ['team'] as const,
    lists: () => [...queryKeys.team.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.team.all, id] as const,
  },
} as const;

/**
 * Типы для ключей запросов
 */
export type QueryKeys = typeof queryKeys;
